<template>
  <div class="excel-session-runner">
    <Notification :text="notification.text" :type="notification.type" />

    <!-- ==================== STEP 1: DATA SOURCE (Splash) ==================== -->
    <div v-if="wizardStep === 1" class="wizard-splash">
      <div class="splash-content">
        <v-icon size="64" color="primary" class="mb-4">mdi-file-excel-box</v-icon>
        <h2>Excel Session Runner</h2>
        <p class="splash-subtitle">Upload a spreadsheet or select an existing file to get started</p>

        <!-- Parsing overlay -->
        <div v-if="state.parsingFile" class="parsing-overlay">
          <v-progress-circular indeterminate size="40" color="primary"></v-progress-circular>
          <div class="mt-2 caption">Parsing file...</div>
        </div>

        <!-- Dropzone -->
        <div
          v-else
          class="file-dropzone"
          :class="{ 'drag-over': isDragging }"
          @dragover.prevent="isDragging = true"
          @dragleave="isDragging = false"
          @drop.prevent="handleFileDrop"
          @click="$refs.fileInput.click()"
        >
          <input ref="fileInput" type="file" accept=".xls,.xlsx,.ods" hidden @change="onNativeFileSelected" />
          <v-icon size="40" color="grey lighten-1">mdi-cloud-upload-outline</v-icon>
          <div class="dropzone-text">
            <strong>Drop your Excel file here</strong>
            <span>or click to browse</span>
          </div>
          <div class="dropzone-hint">.xlsx, .xls, .ods</div>
        </div>

        <!-- Divider -->
        <div v-if="state.existingFiles.length > 0" class="splash-divider">
          <span>or select an existing file</span>
        </div>

        <!-- Existing files list -->
        <div v-if="state.existingFiles.length > 0" class="existing-files-list">
          <div v-if="state.loadingFiles" class="text-center py-3">
            <v-progress-circular indeterminate size="20" color="primary"></v-progress-circular>
          </div>
          <div
            v-for="file in state.existingFiles"
            :key="file.file_id"
            class="file-card"
            @click="selectExistingFile(file)"
          >
            <v-icon color="green darken-1">mdi-file-excel</v-icon>
            <div class="file-card-info">
              <div class="file-card-name">{{ file.file_name }}</div>
              <div class="file-card-meta">{{ formatFileSize(file.file_size) }}</div>
            </div>
            <v-icon small color="grey lighten-1" class="ml-auto">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- ==================== STEP 2: CONFIGURE ==================== -->
    <div v-else-if="wizardStep === 2" class="wizard-configure">

      <!-- File summary header -->
      <div class="config-header">
        <div class="config-header-left">
          <v-icon color="green darken-1" class="mr-2">mdi-file-excel</v-icon>
          <div>
            <div class="font-weight-medium">{{ state.fileName }}</div>
            <div class="caption grey--text">{{ state.totalRows }} rows, {{ state.headers.length }} columns</div>
          </div>
        </div>
        <v-btn text small @click="wizardStep = 1">
          <v-icon small left>mdi-swap-horizontal</v-icon>
          Change File
        </v-btn>
      </div>

      <!-- Chain Selection -->
      <div class="config-section">
        <div class="config-section-title">Select Chain</div>
        <v-autocomplete
          v-model="state.selectedChainId"
          :items="state.chains"
          item-text="chain_name"
          item-value="prompt_chain_id"
          label="Prompt Chain"
          outlined dense
          :loading="state.loadingChains"
          placeholder="Search chains..."
          clearable
          @change="onChainSelected"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.chain_name }}</v-list-item-title>
              <v-list-item-subtitle v-if="item.description" class="caption">{{ item.description }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
        <div v-if="selectedChainInputs.length > 0" class="mt-1">
          <div class="caption grey--text mb-1">Chain Inputs:</div>
          <v-chip v-for="input in selectedChainInputs" :key="input.key" small label class="mr-1 mb-1" color="blue lighten-4">
            {{ input.label || input.key }}
          </v-chip>
        </div>
      </div>

      <!-- Column Mapping -->
      <div v-if="state.selectedChainId" class="config-section">
        <div class="d-flex align-center mb-2">
          <div class="config-section-title mb-0">Column Mapping</div>
          <v-spacer></v-spacer>
          <v-btn x-small color="primary" text @click="autoSuggestMappings">
            <v-icon x-small left>mdi-auto-fix</v-icon>Auto-Suggest
          </v-btn>
          <v-btn x-small color="primary" dark class="ml-1" @click="addMapping">
            <v-icon x-small left>mdi-plus</v-icon>Add
          </v-btn>
        </div>

        <div v-if="state.columnMappings.length === 0" class="caption grey--text text-center py-2">
          No mappings configured. Click "Auto-Suggest" or "Add" to create mappings.
        </div>

        <v-row v-for="(mapping, mIdx) in state.columnMappings" :key="'m-' + mIdx" dense class="mb-2 align-center">
          <v-col cols="4">
            <v-select
              v-model="mapping.excel_column"
              :items="state.headers"
              label="Excel Column"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="1" class="text-center">
            <v-icon small>mdi-arrow-right</v-icon>
          </v-col>
          <v-col cols="4">
            <v-select
              v-model="mapping.chain_input_key"
              :items="chainInputOptions"
              label="Chain Input"
              outlined dense hide-details
            ></v-select>
          </v-col>
          <v-col cols="2">
            <v-select
              v-model="mapping.transform"
              :items="transformOptions"
              label="Transform"
              outlined dense hide-details clearable
            ></v-select>
          </v-col>
          <v-col cols="1">
            <v-btn icon x-small @click="state.columnMappings.splice(mIdx, 1)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Data Preview (collapsible) -->
      <v-expansion-panels v-if="state.previewRows.length > 0" flat class="config-expansion mb-4">
        <v-expansion-panel>
          <v-expansion-panel-header class="caption font-weight-medium">
            Data Preview (first {{ state.previewRows.length }} rows)
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div style="overflow-x: auto;">
              <table class="preview-table">
                <thead>
                  <tr>
                    <th class="row-num">#</th>
                    <th v-for="(h, hIdx) in state.headers" :key="'th-' + hIdx">{{ h }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rIdx) in state.previewRows" :key="rIdx">
                    <td class="row-num">{{ rIdx + 1 }}</td>
                    <td v-for="(h, hIdx) in state.headers" :key="'td-' + rIdx + '-' + hIdx">{{ row[h] != null ? row[h] : '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- Execution Settings -->
      <div v-if="state.selectedChainId" class="config-section">
        <div class="config-section-title">Execution Settings</div>

        <!-- Execution Mode -->
        <v-radio-group v-model="state.executionMode" row dense hide-details class="mb-3 mt-0">
          <v-radio label="Sequential" value="sequence"></v-radio>
          <v-radio label="Group Parallel" value="group_parallel"></v-radio>
          <v-radio label="Full Parallel" value="parallel"></v-radio>
        </v-radio-group>
        <div class="caption grey--text mb-3">
          <template v-if="state.executionMode === 'sequence'">
            Rows run one after another with shared context between them. Grouping is applied.
          </template>
          <template v-else-if="state.executionMode === 'group_parallel'">
            Rows run concurrently but still receive group context documents. No prior row results are shared.
            <v-text-field
              v-model.number="state.parallelConcurrency"
              label="Concurrent Rows"
              type="number"
              outlined dense hide-details
              :min="1" :max="20"
              class="mt-2" style="max-width: 160px"
            ></v-text-field>
          </template>
          <template v-else>
            All rows execute independently at the same time. Grouping and context sharing are ignored.
          </template>
        </div>

        <v-divider class="mb-3"></v-divider>

        <!-- Row Range -->
        <v-row dense>
          <v-col cols="6">
            <v-text-field
              v-model.number="state.rowStart"
              label="From Row"
              type="number"
              outlined dense
              :min="1"
              :max="state.totalRows"
              :placeholder="'1'"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model.number="state.rowEnd"
              label="To Row"
              type="number"
              outlined dense
              :min="1"
              :max="state.totalRows"
              :placeholder="String(state.totalRows || '')"
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
        <div class="caption grey--text mt-1 mb-3">
          {{ rowRangeLabel }}
        </div>

        <!-- Grouping (sequence and group_parallel modes) -->
        <template v-if="state.executionMode !== 'parallel'">
          <v-divider class="mb-3"></v-divider>
          <div class="caption font-weight-medium mb-2">Grouping (Optional)</div>

          <div v-for="(col, gIdx) in state.groupByColumns" :key="'grp-' + gIdx" class="d-flex align-center mb-2">
            <v-chip x-small label class="mr-2" color="blue-grey lighten-4">L{{ gIdx + 1 }}</v-chip>
            <v-select
              v-model="state.groupByColumns[gIdx]"
              :items="state.headers"
              :label="'Group Level ' + (gIdx + 1)"
              outlined dense hide-details class="flex-grow-1"
            ></v-select>
            <v-btn icon x-small class="ml-1" @click="state.groupByColumns.splice(gIdx, 1)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-btn x-small text color="primary" @click="state.groupByColumns.push('')">
            <v-icon x-small left>mdi-plus</v-icon>Add Group Level
          </v-btn>

          <div v-if="groupPreview.length > 0" class="mt-2">
            <div class="caption grey--text mb-1">Preview:</div>
            <div v-for="gp in groupPreview" :key="gp.key" class="d-flex align-center mb-1">
              <v-icon x-small class="mr-1">mdi-folder-outline</v-icon>
              <span class="caption">{{ gp.label }} ({{ gp.count }})</span>
            </div>

            <!-- Group Context Documents -->
            <div class="mt-3">
              <div class="d-flex align-center mb-2">
                <div class="caption font-weight-medium">Group Context Documents</div>
                <v-spacer></v-spacer>
                <v-btn x-small text color="primary" @click="autoSuggestGroupDocs">
                  <v-icon x-small left>mdi-auto-fix</v-icon>Auto-suggest
                </v-btn>
              </div>
              <div v-for="gp in groupPreview" :key="'gd-' + gp.key" class="group-doc-row mb-2">
                <div class="caption grey--text text--darken-1 mb-1">{{ gp.label }} ({{ gp.count }} rows)</div>
                <div class="d-flex align-center">
                  <v-autocomplete
                    :value="state.groupDocuments[gp.key]?.doc_id"
                    :items="state.groupDocuments[gp.key]?.suggestions || []"
                    :loading="state.groupDocuments[gp.key]?.loading"
                    :search-input.sync="state.groupDocuments[gp.key].searchText"
                    @update:search-input="onGroupDocSearch(gp.key, $event)"
                    item-text="display"
                    item-value="doc_id"
                    outlined dense hide-details
                    placeholder="Search documents..."
                    no-filter
                    clearable
                    class="flex-grow-1"
                    @change="onGroupDocChanged(gp.key, $event)"
                  >
                    <template v-slot:selection>
                      <span v-if="state.groupDocuments[gp.key]?.doc_name" class="caption text-truncate">
                        {{ state.groupDocuments[gp.key].doc_name }} ({{ state.groupDocuments[gp.key].system_number }})
                      </span>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.doc_name }}</v-list-item-title>
                        <v-list-item-subtitle class="caption">{{ item.system_number }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:no-data>
                      <v-list-item>
                        <v-list-item-content>
                          <v-list-item-title class="caption grey--text">Type to search documents...</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-autocomplete>
                </div>
              </div>
            </div>
          </div>

          <!-- Context settings (sequence only — group_parallel doesn't use prior results) -->
          <template v-if="state.executionMode === 'sequence'">
            <v-divider class="my-3"></v-divider>

            <v-select
              v-model="state.contextMode"
              :items="[{ text: 'Summary (lighter)', value: 'summary' }, { text: 'Full (detailed)', value: 'full' }]"
              label="Context Mode"
              outlined dense
              hint="How prior row results are shared with subsequent rows"
              persistent-hint class="mb-3"
            ></v-select>

            <v-text-field
              v-model.number="state.maxContextRows"
              label="Max Context Rows"
              type="number"
              outlined dense
              hint="Sliding window limit for prior row context. Leave empty for unlimited."
              persistent-hint
            ></v-text-field>

            <v-select
              v-if="chainOutputKeys.length > 0"
              v-model="state.contextOutputKeys"
              :items="chainOutputKeys"
              label="Context Output Fields"
              multiple chips small-chips deletable-chips
              outlined dense clearable
              hint="Select which outputs to share as context. Leave empty to share all."
              persistent-hint class="mt-3"
            ></v-select>
          </template>
        </template>
      </div>

      <!-- Execute Button -->
      <div class="config-actions">
        <v-btn
          large color="primary"
          :loading="state.executing"
          :disabled="!canExecute || state.executing"
          @click="executeSession"
        >
          <v-icon left>mdi-play</v-icon>
          Execute {{ state.executionMode === 'parallel' ? 'Parallel' : state.executionMode === 'group_parallel' ? 'Group Parallel' : 'Session' }} ({{ effectiveRowCount }} rows)
        </v-btn>
      </div>
    </div>

    <!-- ==================== STEP 3: RESULTS ==================== -->
    <div v-else-if="wizardStep === 3" class="wizard-results">
      <div class="chain-runner">
        <!-- Session/Parallel Status Bar -->
        <div v-if="executionResults.length > 0 || isSessionRunning" class="session-status-bar">
          <div class="session-status-info">
            <v-progress-circular
              v-if="isSessionRunning"
              :value="progressPercent"
              color="primary"
              size="36"
              width="3"
            >
              <span class="caption font-weight-bold">{{ progressPercent }}%</span>
            </v-progress-circular>
            <v-icon v-else-if="effectiveSessionStatus === 'completed'" color="success" size="36">mdi-check-circle</v-icon>
            <v-icon v-else-if="effectiveSessionStatus === 'failed'" color="error" size="36">mdi-alert-circle</v-icon>

            <div class="ml-3">
              <div class="font-weight-medium">{{ state.executionMode === 'parallel' ? 'Parallel' : state.executionMode === 'group_parallel' ? 'Group Parallel' : 'Session' }} Progress</div>
              <div class="caption grey--text">
                <template v-if="state.executionMode === 'parallel'">
                  {{ completedRowCount + failedRowCount }} of {{ executionResults.length }} rows processed
                </template>
                <template v-else>
                  {{ state.progressCurrent }} of {{ state.progressTotal }} rows processed
                </template>
              </div>
            </div>
          </div>
          <div class="d-flex align-center">
            <span v-if="completedRowCount > 0" class="stat-badge success mr-2">
              {{ completedRowCount }} completed
            </span>
            <span v-if="failedRowCount > 0" class="stat-badge error mr-2">
              {{ failedRowCount }} failed
            </span>
            <span v-if="runningRowCount > 0" class="stat-badge processing mr-2">
              <v-progress-circular indeterminate size="12" width="2" color="white" class="mr-1"></v-progress-circular>
              {{ runningRowCount }} running
            </span>
            <v-chip :color="sessionStatusColor" small dark class="ml-2">{{ effectiveSessionStatus }}</v-chip>
            <v-btn text small class="ml-3" @click="wizardStep = 2">
              <v-icon small left>mdi-cog</v-icon>
              Setup
            </v-btn>
          </div>
        </div>

        <!-- Document Creation Status -->
        <CRDocCreationStatus
          v-if="docCreation.showDocumentCreationStatus.value"
          :requests="docCreation.activeDocumentCreationRequests.value"
          :stats="docCreation.getDocCreationStats.value"
          :get-doc-inputs-fn="docCreation.getDocInputsForRow"
          @back="docCreation.showDocumentCreationStatus.value = false"
        />

        <!-- Multi Result View -->
        <CRResultsMulti
          v-else-if="executionResults.length > 0"
          ref="resultsMultiRef"
          :results="executionResults"
          :selected-result="selectedResult"
          :has-manual-inputs="false"
          :total-selected-items="docSelection.totalSelectedItemsAllRows.value"
          :applying-bulk-documents="applyingBulkDocuments"
          :enable-regeneration="false"
          :is-dialog="false"
          :is-previous-execution="false"
          :get-selected-doc-count-fn="docSelection.getSelectedDocumentCount"
          :get-selected-indices-fn="(rowId) => Array.from(docSelection.getSelectedDocumentItems(rowId))"
          :get-row-checkbox-state-fn="docSelection.getRowCheckboxState"
          @select-result="(r) => { selectedResult = r; }"
          @apply-bulk="handleApplyBulk"
          @select-all-rows="() => docSelection.selectAllInAllRows(getComponentRef)"
          @clear-all-rows="() => docSelection.clearAllInAllRows(getComponentRef)"
          @row-checkbox-click="(rowId) => docSelection.handleRowCheckboxClick(rowId, getComponentRef)"
          @expand-all-doc="handleExpandAllDoc"
          @collapse-all-doc="handleCollapseAllDoc"
          @select-all-doc="handleSelectAllDoc"
          @clear-all-doc="handleClearAllDoc"
          @doc-selection-changed="docSelection.handleDocumentSelectionChanged"
        />
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import axios from 'axios';
import { promptChainApi } from '@/services/promptChainApi';
import Notification from "@/components/common/SnackBar.vue";
import CRResultsMulti from '@/components/chainRunner/CRResultsMulti.vue';
import CRDocCreationStatus from '@/components/chainRunner/CRDocCreationStatus.vue';
import { useNotification } from '@/composables/useNotification';
import { useExecutionPolling } from '@/composables/useExecutionPolling';
import { useDocumentSelection } from '@/composables/useDocumentSelection';
import { useDocumentCreation } from '@/composables/useDocumentCreation';
import { isDocumentResult } from '@/utils/documentResultValidator';
import * as XLSX from 'xlsx';

const props = defineProps({
  node: { type: Object, default: null },
  doc: { type: Object, default: null }
});

const { notification, notify } = useNotification();

// --- Wizard navigation ---
const wizardStep = ref(1);  // 1=Data Source, 2=Configure, 3=Results
const isDragging = ref(false);
const fileInput = ref(null);

// --- Execution results infrastructure (shared with composables) ---
const executionResults = ref([]);
const selectedResult = ref(null);
const polledExecutionIds = new Set();
const resultsMultiRef = ref(null);
const applyingBulkDocuments = ref(false);

// --- Composables ---
const polling = useExecutionPolling(executionResults, {
  onCompleted(result, idx, meta) {
    if (!meta.isPartialRegen && isDocumentResult(result)) {
      nextTick(() => {
        docSelection.expandAllDocumentItems(result.rowId, result.result, getComponentRef);
        nextTick(() => {
          docSelection.selectAllDocumentItems(result.rowId, result.result, getComponentRef);
        });
      });
    }
    if (selectedResult.value?.rowId === result.rowId) {
      selectedResult.value = result;
    }
  },
  onFailed(result) {
    if (selectedResult.value?.rowId === result.rowId) {
      selectedResult.value = result;
    }
  },
  onProgress(result) {
    if (selectedResult.value?.rowId === result.rowId) {
      selectedResult.value = result;
    }
  },
  notify
});

const docSelection = useDocumentSelection(executionResults);
const docCreation = useDocumentCreation();

// --- Component ref helper ---
function getComponentRef(rowId) {
  return resultsMultiRef.value?.getDocumentRef(rowId) || null;
}

// --- Document action handlers ---
function handleExpandAllDoc() {
  const result = selectedResult.value || executionResults.value[0];
  if (result && isDocumentResult(result)) {
    docSelection.expandAllDocumentItems(result.rowId, result.result, getComponentRef);
  }
}

function handleCollapseAllDoc() {
  const result = selectedResult.value || executionResults.value[0];
  if (result) docSelection.collapseAllDocumentItems(result.rowId, getComponentRef);
}

function handleSelectAllDoc() {
  const result = selectedResult.value || executionResults.value[0];
  if (result && isDocumentResult(result)) {
    docSelection.selectAllDocumentItems(result.rowId, result.result, getComponentRef);
  }
}

function handleClearAllDoc() {
  const result = selectedResult.value || executionResults.value[0];
  if (result) docSelection.clearAllDocumentItems(result.rowId, getComponentRef);
}

// --- Apply bulk document creation ---
async function handleApplyBulk() {
  const chain = state.selectedChain;
  if (!chain) {
    notify('No chain selected', 'warning');
    return;
  }

  // Build nodeData from chain config — ExcelSessionRunner is a standalone page
  // so props.node/doc aren't available. The backend only needs tmpl_id.
  const tmplId = chain.tmpl_id || chain.trigger_config?.tmpl_id || chain.source_data?.tmpl_id;
  if (!tmplId) {
    notify('Chain has no template configured — cannot create documents', 'warning');
    return;
  }
  const nodeData = { tmpl_id: tmplId, docType: chain.docType || null };

  applyingBulkDocuments.value = true;
  const success = await docCreation.applySelectedBulkDocumentItems(
    executionResults, docSelection, nodeData,
    chain, props.doc || null, notify
  );
  applyingBulkDocuments.value = false;
  if (success) {
    docCreation.showDocumentCreationStatus.value = true;
  }
}

const state = reactive({

  // Chain selection
  chains: [],
  loadingChains: false,
  selectedChainId: null,
  selectedChain: null,
  chainSteps: [],

  // File handling
  file: null,
  fileName: '',
  selectedFileId: null,
  existingFiles: [],
  loadingFiles: false,
  parsingFile: false,
  headers: [],
  previewRows: [],
  allRows: [],
  totalRows: 0,

  // Column mapping
  columnMappings: [],

  // Grouping
  groupByColumns: [],
  groupDocuments: {},  // { [compositeKey]: { doc_id, doc_name, system_number, suggestions, loading, searchText } }

  // Row range
  rowStart: null,
  rowEnd: null,

  // Context settings
  contextMode: 'summary',
  maxContextRows: 20,
  contextOutputKeys: [],

  // Parallel settings
  parallelConcurrency: 5,

  // Execution
  executionMode: 'sequence',  // 'sequence' or 'parallel'
  executing: false,
  sessionExecutionId: null,
  sessionStatus: 'pending',
  progressCurrent: 0,
  progressTotal: 0,
  totalChainSteps: 0,
  rows: [],
  groups: [],
  pollTimer: null,

});

const transformOptions = [
  { text: 'None', value: null },
  { text: 'Trim', value: 'trim' },
  { text: 'Uppercase', value: 'uppercase' },
  { text: 'Lowercase', value: 'lowercase' },
  { text: 'Parse Number', value: 'number' },
  { text: 'Parse JSON', value: 'json_parse' }
];

const selectedChainInputs = computed(() => {
  if (!state.selectedChain) return [];
  const schema = state.selectedChain.input_schema;
  if (!schema) return [];
  try {
    const parsed = typeof schema === 'string' ? JSON.parse(schema) : schema;
    if (parsed.inputs && Array.isArray(parsed.inputs)) {
      return parsed.inputs.filter(i => !i.excluded);
    }
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
});

const chainInputOptions = computed(() => {
  return selectedChainInputs.value.map(i => ({
    text: i.label || i.key,
    value: i.key
  }));
});

const chainOutputKeys = computed(() => {
  if (!state.chainSteps || state.chainSteps.length === 0) return [];
  const keys = [];
  const seen = new Set();
  for (const step of state.chainSteps) {
    if (!step.active && step.active !== undefined) continue;
    const config = step.configuration_data;
    if (!config?.output || !Array.isArray(config.output)) continue;
    const stepName = step.step_name || step.prompt_name || `Step ${step.step_order}`;
    for (const output of config.output) {
      if (output.property && !seen.has(output.property)) {
        seen.add(output.property);
        keys.push({
          text: `${stepName} → ${output.property}`,
          value: output.property
        });
      }
    }
  }
  return keys;
});

const canExecute = computed(() => {
  return state.selectedChainId
    && state.totalRows > 0
    && state.columnMappings.length > 0
    && state.columnMappings.some(m => m.excel_column && m.chain_input_key)
    && !state.executing;
});

const progressPercent = computed(() => {
  if (state.executionMode === 'parallel') {
    const total = executionResults.value.length;
    if (total === 0) return 0;
    const done = executionResults.value.filter(r => r.success !== null).length;
    return Math.round((done / total) * 100);
  }
  // Completed/failed sessions are always 100%
  if (state.sessionStatus === 'completed' || state.sessionStatus === 'failed') return 100;
  if (!state.progressTotal || state.progressTotal === 0) return 0;
  return Math.round((state.progressCurrent / state.progressTotal) * 100);
});

const parallelStatus = computed(() => {
  if (executionResults.value.length === 0) return 'pending';
  const allDone = executionResults.value.every(r => r.success !== null);
  if (!allDone) return 'running';
  const anyFailed = executionResults.value.some(r => r.success === false);
  return anyFailed ? 'failed' : 'completed';
});

const effectiveSessionStatus = computed(() => {
  return state.executionMode === 'parallel' ? parallelStatus.value : state.sessionStatus;
});

const sessionStatusColor = computed(() => {
  switch (effectiveSessionStatus.value) {
    case 'completed': return 'success';
    case 'failed': return 'error';
    case 'running': return 'primary';
    default: return 'grey';
  }
});

const isSessionRunning = computed(() => {
  if (state.executionMode === 'parallel') {
    return executionResults.value.some(r => r.processing);
  }
  return state.sessionStatus === 'running' || state.sessionStatus === 'pending' || state.sessionStatus === 'queued';
});

const completedRowCount = computed(() => {
  if (state.executionMode === 'parallel') {
    return executionResults.value.filter(r => r.success === true).length;
  }
  return state.rows.filter(r => getRowStatus(r) === 'completed').length;
});

const failedRowCount = computed(() => {
  if (state.executionMode === 'parallel') {
    return executionResults.value.filter(r => r.success === false).length;
  }
  return state.rows.filter(r => getRowStatus(r) === 'failed').length;
});

const runningRowCount = computed(() => {
  if (state.executionMode === 'parallel') {
    return executionResults.value.filter(r => r.processing && r.executionId).length;
  }
  return state.rows.filter(r => getRowStatus(r) === 'running').length;
});

const effectiveRowStart = computed(() => {
  const s = state.rowStart;
  return (s && s >= 1) ? Math.min(s, state.totalRows) : 1;
});

const effectiveRowEnd = computed(() => {
  const e = state.rowEnd;
  return (e && e >= 1) ? Math.min(e, state.totalRows) : state.totalRows;
});

const effectiveRowCount = computed(() => {
  if (state.totalRows === 0) return 0;
  return Math.max(0, effectiveRowEnd.value - effectiveRowStart.value + 1);
});

const rowRangeLabel = computed(() => {
  if (state.totalRows === 0) return 'Upload a file to see row count';
  const start = effectiveRowStart.value;
  const end = effectiveRowEnd.value;
  if (start === 1 && end === state.totalRows) {
    return `All ${state.totalRows} rows will be processed`;
  }
  return `Rows ${start}–${end} will be processed (${effectiveRowCount.value} of ${state.totalRows})`;
});

const groupPreview = computed(() => {
  const cols = state.groupByColumns.filter(c => c);
  if (cols.length === 0 || state.allRows.length === 0) return [];

  const groups = new Map();
  for (const row of state.allRows) {
    const keyParts = cols.map(c => String(row[c] ?? 'ungrouped'));
    const displayKey = keyParts.join(' → ');
    const compositeKey = keyParts.join('::');
    if (!groups.has(compositeKey)) {
      groups.set(compositeKey, { displayKey, count: 0 });
    }
    groups.get(compositeKey).count++;
  }

  return Array.from(groups.entries())
    .map(([compositeKey, { displayKey, count }]) => ({ key: compositeKey, label: displayKey, count }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .slice(0, 20);
});

watch(groupPreview, () => {
  initGroupDocuments();
});

onMounted(() => {
  loadChains();
  loadMyFiles();
});

onUnmounted(() => {
  polling.stopAll();
  stopPolling();
});

async function loadChains() {
  state.loadingChains = true;
  try {
    const resp = await axios.get('/externalContent/availablePromptChains');
    if (resp.data?.success !== false) {
      state.chains = resp.data?.chains || resp.data?.data || [];
    }
  } catch (err) {
    console.error('Failed to load chains:', err);
  }
  state.loadingChains = false;
}

async function onChainSelected() {
  state.selectedChain = state.chains.find(c => c.prompt_chain_id === state.selectedChainId) || null;
  state.columnMappings = [];
  state.chainSteps = [];
  state.contextOutputKeys = [];

  if (state.selectedChain && state.selectedChainId) {
    // Load full chain details (steps with output configs)
    try {
      const resp = await axios.get(`/externalContent/promptChain/${state.selectedChainId}`);
      if (resp.data?.success && resp.data.data) {
        const chainData = resp.data.data;
        state.chainSteps = chainData.steps || [];
        // Merge full chain data (includes tmpl_id, trigger info, etc.)
        Object.assign(state.selectedChain, chainData);

        // Apply chain-level execution_config as defaults
        const execConfig = chainData.execution_config;
        if (execConfig) {
          if (execConfig.execution_mode) state.executionMode = execConfig.execution_mode;
          if (execConfig.context_mode) state.contextMode = execConfig.context_mode;
          if (execConfig.max_context_rows !== undefined) state.maxContextRows = execConfig.max_context_rows;
        }

        // Pre-select outputs flagged as context in step configs
        const flaggedKeys = [];
        for (const step of state.chainSteps) {
          const config = step.configuration_data;
          if (!config?.output || !Array.isArray(config.output)) continue;
          for (const output of config.output) {
            if (output.isContextOutput && output.property) {
              flaggedKeys.push(output.property);
            }
          }
        }
        if (flaggedKeys.length > 0) {
          state.contextOutputKeys = flaggedKeys;
        }
      }
    } catch (err) {
      console.error('Failed to load chain details:', err);
    }
  }

  if (state.headers.length > 0 && state.selectedChain) {
    autoSuggestMappings();
  }
}

async function loadMyFiles() {
  state.loadingFiles = true;
  try {
    const resp = await axios.get('/file/myFiles');
    if (resp.data?.success) {
      state.existingFiles = resp.data.files || [];
    }
  } catch (err) {
    console.error('Failed to load files:', err);
  }
  state.loadingFiles = false;
}

// --- File handling (wizard-style) ---

function handleFileDrop(event) {
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (file) {
    state.file = file;
    state.fileName = file.name;
    state.selectedFileId = null;
    parseFile(file);
  }
}

function onNativeFileSelected(event) {
  const file = event.target.files[0];
  if (file) {
    state.file = file;
    state.fileName = file.name;
    state.selectedFileId = null;
    parseFile(file);
  }
}

async function selectExistingFile(file) {
  state.selectedFileId = file.file_id;
  state.fileName = file.file_name;
  state.file = null;
  state.parsingFile = true;
  try {
    const fileResp = await axios.get(`/file/downloadContent/${file.file_id}`, { responseType: 'arraybuffer' });
    const workbook = XLSX.read(fileResp.data);
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet);

    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
    const headers = [];
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cell = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: col })];
      if (cell?.v != null) headers.push(String(cell.v));
    }

    state.headers = headers;
    state.allRows = rows;
    state.previewRows = rows.slice(0, 5);
    state.totalRows = rows.length;

    if (state.selectedChain && state.columnMappings.length === 0) {
      autoSuggestMappings();
    }

    if (state.headers.length > 0) {
      wizardStep.value = 2;
    }
  } catch (err) {
    notify('Failed to load file: ' + (err.message || err), 'error');
  }
  state.parsingFile = false;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

async function parseFile(file) {
  state.parsingFile = true;
  try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer);
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet);

    const range = XLSX.utils.decode_range(sheet['!ref'] || 'A1');
    const headers = [];
    for (let col = range.s.c; col <= range.e.c; col++) {
      const cell = sheet[XLSX.utils.encode_cell({ r: range.s.r, c: col })];
      if (cell?.v != null) headers.push(String(cell.v));
    }

    state.headers = headers;
    state.allRows = rows;
    state.previewRows = rows.slice(0, 5);
    state.totalRows = rows.length;

    if (state.selectedChain && state.columnMappings.length === 0) {
      autoSuggestMappings();
    }

    if (state.headers.length > 0) {
      wizardStep.value = 2;
    }
  } catch (err) {
    notify('Failed to parse Excel file: ' + err.message, 'error');
  }
  state.parsingFile = false;
}

function autoSuggestMappings() {
  if (!selectedChainInputs.value.length || !state.headers.length) return;

  const mappings = [];
  for (const input of selectedChainInputs.value) {
    const inputKey = (input.key || '').toLowerCase().replace(/[_\s-]/g, '');
    const inputLabel = (input.label || '').toLowerCase().replace(/[_\s-]/g, '');

    let bestMatch = null;
    for (const header of state.headers) {
      const headerNorm = header.toLowerCase().replace(/[_\s-]/g, '');
      if (headerNorm === inputKey || headerNorm === inputLabel) {
        bestMatch = header;
        break;
      }
      if (!bestMatch && (headerNorm.includes(inputKey) || inputKey.includes(headerNorm)
          || headerNorm.includes(inputLabel) || inputLabel.includes(headerNorm))) {
        bestMatch = header;
      }
    }

    if (bestMatch) {
      mappings.push({
        excel_column: bestMatch,
        chain_input_key: input.key,
        transform: null
      });
    }
  }

  if (mappings.length > 0) {
    state.columnMappings = mappings;
  }
}

function addMapping() {
  state.columnMappings.push({
    excel_column: '',
    chain_input_key: '',
    transform: null
  });
}

function applyTransform(value, transform) {
  if (value == null) return value;
  const str = String(value);
  switch (transform) {
    case 'trim': return str.trim();
    case 'uppercase': return str.toUpperCase();
    case 'lowercase': return str.toLowerCase();
    case 'number': return Number(str) || 0;
    case 'json_parse': try { return JSON.parse(str); } catch { return str; }
    default: return value;
  }
}

// --- Group Context Documents ---

let _searchDebounceTimers = {};

function initGroupDocuments() {
  const newDocs = {};
  for (const gp of groupPreview.value) {
    const existing = state.groupDocuments[gp.key];
    newDocs[gp.key] = existing || { doc_id: null, doc_name: null, system_number: null, suggestions: [], loading: false, searchText: '' };
  }
  state.groupDocuments = newDocs;
}

async function searchGroupDoc(compositeKey, searchText) {
  if (!searchText || searchText.length < 2) return;
  const entry = state.groupDocuments[compositeKey];
  if (!entry) return;
  entry.loading = true;
  try {
    const resp = await axios.post('/document/filterDocs', {
      filterText: searchText,
      pageSize: 10,
      returnFields: ['doc_id', 'doc_name', 'system_number']
    });
    const docs = resp.data?.Data?.documents || resp.data?.documents || [];
    entry.suggestions = docs.map(d => ({
      doc_id: d.doc_id,
      doc_name: d.doc_name,
      system_number: d.system_number,
      display: `${d.doc_name} (${d.system_number})`
    }));
  } catch (err) {
    console.error('Group doc search error:', err);
    entry.suggestions = [];
  }
  entry.loading = false;
}

function onGroupDocSearch(compositeKey, searchText) {
  clearTimeout(_searchDebounceTimers[compositeKey]);
  _searchDebounceTimers[compositeKey] = setTimeout(() => searchGroupDoc(compositeKey, searchText), 400);
}

function onGroupDocChanged(compositeKey, docId) {
  const entry = state.groupDocuments[compositeKey];
  if (!entry) return;
  if (!docId) {
    // Cleared
    entry.doc_id = null;
    entry.doc_name = null;
    entry.system_number = null;
    entry.searchText = '';
    return;
  }
  const match = (entry.suggestions || []).find(s => s.doc_id === docId);
  if (match) {
    entry.doc_id = match.doc_id;
    entry.doc_name = match.doc_name;
    entry.system_number = match.system_number;
  }
}

async function autoSuggestGroupDocs() {
  for (const gp of groupPreview.value) {
    const entry = state.groupDocuments[gp.key];
    if (!entry || entry.doc_id) continue;  // Skip already assigned
    // Search by the first group-level value (before any '::')
    const firstValue = gp.key.split('::')[0];
    if (!firstValue || firstValue === 'ungrouped') continue;
    entry.loading = true;
    try {
      const resp = await axios.post('/document/filterDocs', {
        filterText: firstValue,
        pageSize: 5,
        returnFields: ['doc_id', 'doc_name', 'system_number']
      });
      const docs = resp.data?.Data?.documents || resp.data?.documents || [];
      entry.suggestions = docs.map(d => ({
        doc_id: d.doc_id,
        doc_name: d.doc_name,
        system_number: d.system_number,
        display: `${d.doc_name} (${d.system_number})`
      }));
      // Auto-select if exactly one match
      if (docs.length === 1) {
        entry.doc_id = docs[0].doc_id;
        entry.doc_name = docs[0].doc_name;
        entry.system_number = docs[0].system_number;
      }
    } catch (err) {
      console.error(`Auto-suggest failed for "${firstValue}":`, err);
    }
    entry.loading = false;
  }
}

function buildGroupDocPayload() {
  const docs = {};
  for (const [key, config] of Object.entries(state.groupDocuments)) {
    if (config.doc_id) {
      docs[key] = { doc_id: config.doc_id };
    }
  }
  return Object.keys(docs).length > 0 ? docs : undefined;
}

function buildRowInputs(excelRow, validMappings) {
  const manualValues = {};
  for (const mapping of validMappings) {
    let value = excelRow[mapping.excel_column];
    if (mapping.transform) {
      value = applyTransform(value, mapping.transform);
    }
    manualValues[mapping.chain_input_key] = value;
  }
  return manualValues;
}

async function executeSession() {
  // Build placeholders and transition to results immediately
  const rowCount = effectiveRowCount.value;
  resetExecutionState();
  state.executing = true;
  state.sessionStatus = 'running';
  state.progressCurrent = 0;
  state.progressTotal = rowCount;
  state.rows = [];
  state.groups = [];

  const validMappings = state.columnMappings.filter(m => m.excel_column && m.chain_input_key);
  const startIdx = effectiveRowStart.value - 1;
  const endIdx = effectiveRowEnd.value;
  const rowsToProcess = state.allRows.slice(startIdx, endIdx);

  const placeholders = rowsToProcess.map((excelRow, i) => ({
    rowNumber: i + 1,
    rowId: i + 1,
    result: null,
    inputs: { manual: buildRowInputs(excelRow, validMappings), hierarchies: {} },
    success: null,
    processing: true,
    executionId: null,
    progress: null,
    currentStep: 'Resolving...',
    chainName: state.selectedChain?.chain_name || null,
    groupKey: null
  }));
  executionResults.value = placeholders;
  selectedResult.value = placeholders[0] || null;

  state.sessionExecutionId = state.executionMode === 'parallel' ? 'parallel' : 'pending';
  wizardStep.value = 3;

  // Now run execution in background
  if (state.executionMode === 'parallel') {
    executeParallelRows(validMappings, rowsToProcess);
  } else {
    // Both 'sequence' and 'group_parallel' use the session engine
    executeSequenceSession(validMappings);
  }
}

async function executeSequenceSession(validMappings) {
  try {
    let fileId;

    if (state.selectedFileId) {
      fileId = state.selectedFileId;
    } else {
      // Update all rows to show uploading status
      updateAllRowSteps('Uploading file...');
      const formData = new FormData();
      formData.append('file', state.file);

      const uploadResp = await axios.post('/file/uploadSimple', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!uploadResp.data?.success) {
        throw new Error(uploadResp.data?.error || 'File upload failed');
      }
      fileId = uploadResp.data.file_id;
    }

    updateAllRowSteps('Starting session...');

    const isGroupParallel = state.executionMode === 'group_parallel';
    const payload = {
      prompt_chain_id: state.selectedChainId,
      file_id: fileId,
      column_mappings: validMappings,
      group_by_columns: state.groupByColumns.filter(c => c) || null,
      sheet_index: 0,
      row_context_mode: isGroupParallel ? 'none' : state.contextMode,
      max_context_rows: isGroupParallel ? 0 : (state.maxContextRows || 20),
      row_start: effectiveRowStart.value !== 1 ? effectiveRowStart.value : undefined,
      row_end: effectiveRowEnd.value !== state.totalRows ? effectiveRowEnd.value : undefined,
      context_output_keys: isGroupParallel ? undefined : (state.contextOutputKeys.length > 0 ? state.contextOutputKeys : undefined),
      group_context_documents: buildGroupDocPayload(),
      parallel_rows: isGroupParallel,
      parallel_concurrency: isGroupParallel ? (state.parallelConcurrency || 5) : undefined
    };

    const resp = await promptChainApi.executeExcelSession(payload);

    if (resp.data?.success) {
      state.sessionExecutionId = resp.data.sessionExecutionId;
      updateAllRowSteps('Queued...');
      notify('Session started successfully', 'success');
      startPolling();
    } else {
      throw new Error(resp.data?.error || 'Failed to start session');
    }
  } catch (err) {
    notify('Execution failed: ' + (err.message || err), 'error');
    state.sessionStatus = 'failed';
    updateAllRowSteps('Failed to start');
    markAllRowsFailed(err.message || String(err));
  }

  state.executing = false;
}

async function executeParallelRows(validMappings, rowsToProcess) {
  try {
    const rowCount = rowsToProcess.length;
    const chain = state.selectedChain;

    // Build source_data to match what the sequence/backend builds
    const tmplId = chain.tmpl_id || chain.trigger_config?.tmpl_id || chain.source_data?.tmpl_id || null;
    const baseSourceData = {
      tmpl_id: tmplId,
      ...(state.selectedFileId && { file_id: state.selectedFileId }),
      excel_config: {
        column_mappings: validMappings,
        group_by: [],
        sheet_index: 0,
        context_mode: 'none',
        max_context_iterations: 0,
        share_context: false
      }
    };

    const baseTriggerContext = {
      trigger_type: 'excel_file',
      trigger_id: 0
    };

    // Fire all requests concurrently
    const promises = rowsToProcess.map((_excelRow, i) => {
      const manualValues = executionResults.value[i].inputs.manual;

      const payload = {
        prompt_chain_id: chain.prompt_chain_id,
        trigger_context: {
          ...baseTriggerContext,
          source_data: {
            ...baseSourceData,
            iteration_index: i,
            iteration_total: rowCount
          },
          manual_values: manualValues
        }
      };

      return promptChainApi.executeChain(payload)
        .then(response => {
          if (response.data?.executionId) {
            const executionId = response.data.executionId;
            updateRow(i, { executionId, currentStep: 'Starting...' });
            polling.startPolling(executionId, i);
          }
        })
        .catch(err => {
          console.error(`Error starting execution for row ${i + 1}:`, err);
          updateRow(i, { success: false, processing: false, error: err.message, currentStep: 'Failed to start' });
        });
    });

    await Promise.all(promises);

    notify(`${rowCount} parallel executions started. Results will update automatically.`, 'success');
  } catch (err) {
    notify('Parallel execution failed: ' + (err.message || err), 'error');
    state.sessionStatus = 'failed';
  }

  state.executing = false;
}

function updateRow(index, updates) {
  executionResults.value = [
    ...executionResults.value.slice(0, index),
    { ...executionResults.value[index], ...updates },
    ...executionResults.value.slice(index + 1)
  ];
}

function updateAllRowSteps(step) {
  executionResults.value = executionResults.value.map(r =>
    r.processing ? { ...r, currentStep: step } : r
  );
}

function markAllRowsFailed(error) {
  executionResults.value = executionResults.value.map(r =>
    r.processing ? { ...r, success: false, processing: false, error } : r
  );
}

function resetExecutionState() {
  executionResults.value = [];
  selectedResult.value = null;
  polledExecutionIds.clear();
  docSelection.selectedDocumentItems.value = new Map();
  docSelection.selectedResultRows.value = new Set();
  docCreation.showDocumentCreationStatus.value = false;
  polling.stopAll();
}

function startPolling() {
  stopPolling();
  pollStatus();
  state.pollTimer = setInterval(pollStatus, 2000);
}

function stopPolling() {
  if (state.pollTimer) {
    clearInterval(state.pollTimer);
    state.pollTimer = null;
  }
}

function parseIterationData(data) {
  if (!data) return {};
  try {
    return typeof data === 'string' ? JSON.parse(data) : data;
  } catch {
    return {};
  }
}

async function pollStatus() {
  if (!state.sessionExecutionId) return;

  try {
    const resp = await promptChainApi.getExcelSessionStatus(state.sessionExecutionId);
    const data = resp.data;

    if (data?.success) {
      const sessionData = data.data;
      state.sessionStatus = sessionData.session?.status || state.sessionStatus;
      state.progressCurrent = sessionData.progress?.current || 0;
      state.progressTotal = sessionData.progress?.total || state.progressTotal;
      state.totalChainSteps = sessionData.totalChainSteps || state.totalChainSteps;
      state.rows = sessionData.rows || [];
      state.groups = sessionData.groups || [];

      for (const row of state.rows) {
        const execId = row.iteration_execution_id;
        const resultIndex = row.iteration_index;
        const existing = executionResults.value[resultIndex];

        if (!existing) continue;

        if (execId && !polledExecutionIds.has(execId)) {
          // New execution ID found — set up and start individual polling
          polledExecutionIds.add(execId);

          // Only show mapped input keys as chips, not every column from the spreadsheet
          const allIterData = parseIterationData(row.iteration_data);
          const mappedKeys = new Set(state.columnMappings
            .filter(m => m.excel_column && m.chain_input_key)
            .map(m => m.chain_input_key));
          const filteredInputs = {};
          for (const [k, v] of Object.entries(allIterData)) {
            if (mappedKeys.has(k)) filteredInputs[k] = v;
          }

          executionResults.value = [
            ...executionResults.value.slice(0, resultIndex),
            {
              ...existing,
              executionId: execId,
              inputs: {
                manual: filteredInputs,
                hierarchies: {}
              },
              currentStep: 'Starting...',
              groupKey: row.group_key || null
            },
            ...executionResults.value.slice(resultIndex + 1)
          ];

          polling.startPolling(execId, resultIndex);
        } else if (!execId && existing.processing) {
          // Row still pending — show queue position
          const pendingRows = state.rows.filter(r => !r.iteration_execution_id);
          const posInQueue = pendingRows.findIndex(r => r.iteration_index === resultIndex) + 1;
          const queueLabel = pendingRows.length > 1
            ? `Queued (${posInQueue} of ${pendingRows.length} waiting)...`
            : 'Queued — next up...';

          if (existing.currentStep !== queueLabel) {
            executionResults.value = [
              ...executionResults.value.slice(0, resultIndex),
              { ...existing, currentStep: queueLabel },
              ...executionResults.value.slice(resultIndex + 1)
            ];
          }
        }
      }

      if (state.sessionStatus === 'completed' || state.sessionStatus === 'failed') {
        stopPolling();
        if (state.sessionStatus === 'completed') {
          notify('Session completed successfully!', 'success');
        } else {
          notify('Session failed', 'error');
        }
      }
    }
  } catch (err) {
    console.error('Polling error:', err);
  }
}

function getRowStatus(row) {
  return row.execution_status || row.status || 'pending';
}

</script>

<style scoped>
.excel-session-runner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px;
}

/* ===== Step 1: Splash ===== */
.wizard-splash {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.splash-content {
  text-align: center;
  max-width: 560px;
  width: 100%;
}

.splash-content h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.splash-subtitle {
  color: #888;
  font-size: 0.9rem;
  margin-bottom: 32px;
}

.parsing-overlay {
  padding: 40px;
}

.file-dropzone {
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 40px 24px;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
}

.file-dropzone:hover,
.file-dropzone.drag-over {
  border-color: #1976D2;
  background: #E3F2FD;
}

.dropzone-text {
  margin-top: 12px;
}

.dropzone-text strong {
  display: block;
  font-size: 0.95rem;
}

.dropzone-text span {
  color: #999;
  font-size: 0.85rem;
}

.dropzone-hint {
  margin-top: 8px;
  color: #bbb;
  font-size: 0.75rem;
}

.splash-divider {
  display: flex;
  align-items: center;
  margin: 24px 0;
  color: #bbb;
  font-size: 0.8rem;
}

.splash-divider::before,
.splash-divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e0e0e0;
}

.splash-divider span {
  padding: 0 12px;
}

.existing-files-list {
  max-height: 280px;
  overflow-y: auto;
}

.file-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
}

.file-card:hover {
  border-color: #1976D2;
  background: #f8fbff;
}

.file-card-name {
  font-weight: 500;
  font-size: 0.85rem;
}

.file-card-meta {
  font-size: 0.75rem;
  color: #999;
}

/* ===== Step 2: Configure ===== */
.wizard-configure {
  max-width: 900px;
  margin: 0 auto;
}

.config-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 20px;
}

.config-header-left {
  display: flex;
  align-items: center;
}

.config-section {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
}

.config-section-title {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 12px;
}

.config-expansion {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
}

.config-actions {
  text-align: center;
  padding: 24px 0 8px;
}

/* ===== Step 3: Results ===== */
.wizard-results {
  width: 100%;
}

/* ===== Preview Table ===== */
.preview-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}

.preview-table th,
.preview-table td {
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  text-align: left;
  white-space: nowrap;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-table th {
  background: #f5f5f5;
  font-weight: 600;
}

.preview-table .row-num {
  width: 30px;
  text-align: center;
  color: #999;
}

/* ===== Stat Badges ===== */
.stat-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: white;
}
.stat-badge.success { background: #4CAF50; }
.stat-badge.error { background: #F44336; }
.stat-badge.processing { background: #2196F3; }

/* ===== Session Status Bar ===== */
.session-status-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 8px;
}

.session-status-info {
  display: flex;
  align-items: center;
}

</style>

<style lang="scss">
/* Dark mode — unscoped so .v-application ancestor selector works */
.v-application.theme--dark .excel-session-runner {
  .session-status-bar {
    background: #2a2a2a;
    border-color: #444;
  }
  .session-status-info .caption.grey--text {
    color: #aaa !important;
  }
}
</style>
